import React from "react";

import { useIsMutating } from "@tanstack/react-query";
import { Alert, Flex, Spin, Typography } from "antd";
import { Outlet, useLocation, useParams } from "react-router-dom";

import { useAppShellHeader } from "@/components";

import { ExperimentFlowActions } from "./ExperimentFlowActions";
import { ExperimentFlowStepper } from "./ExperimentFlowStepper";
import {
  getExperimentApiErrorMessage,
  PROTOTYPE_BUILD_MUTATION_KEY,
  resolvePrototypeIterationIdFromDetail,
  type PrototypeDetailResponse,
  useConfirmPrototypeDeployment,
  usePrototypeByExperiment,
} from "../api/experimentsApi";
import { ExperimentEditorProvider } from "../context/ExperimentEditorContext";
import { useExperimentEditor } from "../hooks/useExperimentEditor";
import { EXPERIMENT_FLOW_STEPS, getExperimentStepByPathname } from "../routes";
import { validateContextStep } from "../validations/contextValidation";
import { validateResearchStep } from "../validations/researchValidation";

const normalizePrototypeStatus = (status?: string | number | null) => {
  if (typeof status === "number") {
    switch (status) {
      case 0:
        return "queued";
      case 1:
        return "processing";
      case 2:
        return "completed";
      case 3:
        return "failed";
      default:
        return String(status).trim().toLowerCase();
    }
  }

  return (status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
};

const isActivePrototypeStatus = (status?: string | number | null) => {
  return [
    "pending",
    "processing",
    "queued",
    "submitting",
    "submitted",
    "running",
    "in_progress",
    "inprogress",
    "building",
  ].includes(normalizePrototypeStatus(status));
};

const normalizePrototypeVersionStatus = (status?: string | number | null) => {
  if (typeof status === "number") {
    switch (status) {
      case 0:
        return "processing";
      case 1:
        return "completed";
      case 2:
        return "failed";
      default:
        return String(status).trim().toLowerCase();
    }
  }

  return normalizePrototypeStatus(status);
};

const isCompletedPrototypeVersionStatus = (status?: string | number | null) => {
  return ["completed", "complete", "done", "succeeded", "success"].includes(
    normalizePrototypeVersionStatus(status),
  );
};

const isActivePrototypeVersionStatus = (status?: string | number | null) => {
  return [
    "pending",
    "processing",
    "queued",
    "submitting",
    "submitted",
    "running",
    "in_progress",
    "inprogress",
    "building",
  ].includes(normalizePrototypeVersionStatus(status));
};

const hasPrototypeVersionOutput = (
  version: NonNullable<PrototypeDetailResponse["versions"]>[number],
) => {
  if (isActivePrototypeVersionStatus(version.status)) {
    return false;
  }

  return (
    Boolean(version.v0DemoUrl?.trim()) ||
    Boolean(version.completedDate?.trim()) ||
    isCompletedPrototypeVersionStatus(version.status)
  );
};

const hasGeneratedPrototypeOutput = (
  prototype?: PrototypeDetailResponse | null,
) => {
  if (!prototype) {
    return false;
  }

  if (isActivePrototypeStatus(prototype.status)) {
    return false;
  }

  const currentVersion = (prototype.versions ?? []).find(
    (version) => version.isCurrentVersion,
  );

  if (currentVersion) {
    if (isActivePrototypeVersionStatus(currentVersion.status)) {
      return false;
    }

    if (hasPrototypeVersionOutput(currentVersion)) {
      return true;
    }
  }

  if (
    prototype.currentVersionId?.trim() ||
    prototype.v0DemoUrl?.trim() ||
    prototype.v0WebUrl?.trim()
  ) {
    return true;
  }

  return (prototype.versions ?? []).some((version) =>
    hasPrototypeVersionOutput(version),
  );
};

const hasActivePrototypeBuild = (
  prototype?: PrototypeDetailResponse | null,
) => {
  if (!prototype) {
    return false;
  }

  if (isActivePrototypeStatus(prototype.status)) {
    return true;
  }

  return (prototype.versions ?? []).some(
    (version) =>
      version.isCurrentVersion &&
      isActivePrototypeVersionStatus(version.status),
  );
};

const ExperimentEditorShell: React.FC = () => {
  const location = useLocation();
  const currentStepKey = getExperimentStepByPathname(location.pathname);
  const currentStepIndex =
    EXPERIMENT_FLOW_STEPS.find((step) => step.key === currentStepKey)?.index ??
    1;
  const {
    canAccessStep,
    clearSubmissionError,
    experimentData,
    experimentId,
    isExperimentViewOnly,
    isCreatingExperiment,
    isLoading,
    isUpdatingExperiment,
    navigateToStep,
    runOrchestration,
    saveContextStep,
    saveResearchStep,
    selectedExperiment,
    submissionError,
  } = useExperimentEditor();
  const [prototypeContinueError, setPrototypeContinueError] = React.useState<
    string | null
  >(null);
  const resolvedExperimentId =
    experimentId ?? experimentData.experimentId ?? undefined;
  const {
    mutateAsync: confirmPrototypeDeployment,
    isPending: isConfirmingPrototypeDeployment,
  } = useConfirmPrototypeDeployment();
  const prototypeDetailQuery = usePrototypeByExperiment(resolvedExperimentId, {
    enabled: Boolean(resolvedExperimentId),
  });
  const activePrototypeBuildMutationCount = useIsMutating({
    mutationKey: PROTOTYPE_BUILD_MUTATION_KEY,
  });
  const confirmPrototypeDeploymentRef = React.useRef(
    confirmPrototypeDeployment,
  );
  const prototypeIterationId = React.useMemo(
    () => resolvePrototypeIterationIdFromDetail(prototypeDetailQuery.data),
    [prototypeDetailQuery.data],
  );
  const hasGeneratedPrototype = React.useMemo(
    () => hasGeneratedPrototypeOutput(prototypeDetailQuery.data),
    [prototypeDetailQuery.data],
  );
  const isPrototypeBuildActive = React.useMemo(
    () => hasActivePrototypeBuild(prototypeDetailQuery.data),
    [prototypeDetailQuery.data],
  );
  const isPrototypeDetailLoading =
    prototypeDetailQuery.isLoading && !prototypeDetailQuery.data;
  const canOpenTagsFromPrototype =
    Boolean(prototypeIterationId) &&
    hasGeneratedPrototype &&
    !isPrototypeDetailLoading &&
    activePrototypeBuildMutationCount === 0 &&
    !isPrototypeBuildActive &&
    !isConfirmingPrototypeDeployment;
  const coreResearchQuestionIds = React.useMemo(
    () =>
      new Set(
        (selectedExperiment?.premises ?? []).flatMap((premise) => {
          const premiseId = premise.id?.trim();
          return premise.type === 1 && premiseId ? [premiseId] : [];
        }),
      ),
    [selectedExperiment?.premises],
  );
  const researchStepValidation = React.useMemo(
    () =>
      validateResearchStep({
        researchObjective: experimentData.researchObjective,
        primaryHypothesis: experimentData.primaryHypothesis,
      }),
    [experimentData.primaryHypothesis, experimentData.researchObjective],
  );
  const contextStepValidation = React.useMemo(
    () =>
      validateContextStep({
        name: experimentData.name,
        goal: experimentData.goal,
        description: experimentData.description,
      }),
    [experimentData.description, experimentData.goal, experimentData.name],
  );
  const arePrototypeQuestionsReadyForFinalize = React.useMemo(() => {
    const prototypeQuestions = (experimentData.feedbackFlow?.questions ?? []).filter(
      (question) => question.section === "prototype",
    );

    return (
      prototypeQuestions.length > 0 &&
      prototypeQuestions.every((question) => {
        const premiseId = question.premiseId?.trim();

        if (!question.question.trim() || !premiseId) {
          return false;
        }

        return (
          coreResearchQuestionIds.size === 0 ||
          coreResearchQuestionIds.has(premiseId)
        );
      })
    );
  }, [coreResearchQuestionIds, experimentData.feedbackFlow?.questions]);

  React.useEffect(() => {
    setPrototypeContinueError(null);
  }, [currentStepKey]);

  React.useEffect(() => {
    confirmPrototypeDeploymentRef.current = confirmPrototypeDeployment;
  }, [confirmPrototypeDeployment]);

  const handleContinue = React.useCallback(async () => {
    setPrototypeContinueError(null);
    clearSubmissionError();

    if (isExperimentViewOnly) {
      const nextStep = EXPERIMENT_FLOW_STEPS.find(
        (step) => step.index === currentStepIndex + 1,
      );

      if (nextStep) {
        navigateToStep(nextStep.key);
      }
      return;
    }

    if (currentStepKey === "context") {
      if (!contextStepValidation.isValid) {
        return;
      }

      const resolvedExperimentId = await saveContextStep();
      if (resolvedExperimentId) {
        navigateToStep("research", resolvedExperimentId);
      }
      return;
    }

    if (currentStepKey === "research") {
      if (!researchStepValidation.isValid) {
        return;
      }

      const wasSaved = await saveResearchStep();
      if (wasSaved) {
        const targetExperimentId =
          experimentId ?? experimentData.experimentId ?? "";
        if (targetExperimentId) {
          void runOrchestration(targetExperimentId);
        }
        navigateToStep("prototype");
      }
      return;
    }

    if (currentStepKey === "prototype") {
      if (!resolvedExperimentId) {
        setPrototypeContinueError(
          "Experiment ID missing. Save the experiment before continuing.",
        );
        return;
      }

      if (activePrototypeBuildMutationCount > 0 || isPrototypeBuildActive) {
        setPrototypeContinueError(
          "Prototype changes are still being applied. Wait for the latest build to finish before continuing to tagging.",
        );
        return;
      }

      if (!prototypeIterationId || !hasGeneratedPrototype) {
        setPrototypeContinueError(
          isPrototypeDetailLoading
            ? "Prototype details are still loading. Try again in a moment."
            : "Generate a prototype before continuing to tagging.",
        );
        return;
      }

      try {
        await confirmPrototypeDeploymentRef.current({
          experimentId: resolvedExperimentId,
          iterationId: prototypeIterationId,
        });
      } catch (error) {
        setPrototypeContinueError(
          getExperimentApiErrorMessage(
            error,
            "Failed to confirm prototype deployment.",
          ),
        );
        return;
      }

      navigateToStep("tags");
      return;
    }

    if (currentStepKey === "tags") {
      if (!arePrototypeQuestionsReadyForFinalize) {
        return;
      }

      navigateToStep("finalize");
    }
  }, [
    clearSubmissionError,
    currentStepKey,
    currentStepIndex,
    experimentData.experimentId,
    experimentId,
    navigateToStep,
    arePrototypeQuestionsReadyForFinalize,
    activePrototypeBuildMutationCount,
    hasGeneratedPrototype,
    isExperimentViewOnly,
    isPrototypeDetailLoading,
    isPrototypeBuildActive,
    prototypeIterationId,
    resolvedExperimentId,
    runOrchestration,
    saveContextStep,
    saveResearchStep,
    contextStepValidation.isValid,
    researchStepValidation.isValid,
  ]);

  const handlePrevious = React.useCallback(() => {
    if (currentStepIndex <= 1) {
      return;
    }

    const previousStep = EXPERIMENT_FLOW_STEPS.find(
      (step) => step.index === currentStepIndex - 1,
    );
    if (!previousStep) {
      return;
    }

    navigateToStep(previousStep.key);
  }, [currentStepIndex, navigateToStep]);

  const handleStepClick = React.useCallback(
    (stepKey: (typeof EXPERIMENT_FLOW_STEPS)[number]["key"]) => {
      if (stepKey === currentStepKey) {
        return;
      }

      if (isExperimentViewOnly) {
        navigateToStep(stepKey);
        return;
      }

      if (stepKey === "tags" && currentStepKey === "prototype") {
        if (!canOpenTagsFromPrototype) {
          return;
        }

        void handleContinue();
        return;
      }

      if (stepKey === "tags" && !hasGeneratedPrototype) {
        return;
      }

      navigateToStep(stepKey);
    },
    [
      canOpenTagsFromPrototype,
      currentStepKey,
      handleContinue,
      hasGeneratedPrototype,
      isExperimentViewOnly,
      navigateToStep,
    ],
  );

  const headerCenterContent = React.useMemo(
    () =>
      isLoading ? null : (
        <ExperimentFlowStepper
          currentStepKey={currentStepKey}
          onStepClick={handleStepClick}
          canAccessStep={canAccessStep}
          isStepDisabled={(stepKey) =>
            !isExperimentViewOnly &&
            ((stepKey === "tags" &&
              (currentStepKey === "prototype"
                ? !canOpenTagsFromPrototype
                : !hasGeneratedPrototype)) ||
              (stepKey === "finalize" && currentStepKey !== "finalize"))
          }
        />
      ),
    [
      canAccessStep,
      canOpenTagsFromPrototype,
      currentStepKey,
      handleStepClick,
      hasGeneratedPrototype,
      isExperimentViewOnly,
      isLoading,
    ],
  );

  const flowActions = React.useMemo(
    () =>
      isLoading ? null : (
        <ExperimentFlowActions
          currentStepKey={currentStepKey}
          canContinue={
            isExperimentViewOnly ||
            !(
              currentStepKey === "finalize" ||
              (currentStepKey === "context" &&
                (isCreatingExperiment ||
                  isUpdatingExperiment ||
                  !contextStepValidation.isValid)) ||
              (currentStepKey === "research" &&
                (isUpdatingExperiment || !researchStepValidation.isValid)) ||
              (currentStepKey === "prototype" && !canOpenTagsFromPrototype) ||
              (currentStepKey === "tags" &&
                !arePrototypeQuestionsReadyForFinalize)
            )
          }
          isCreating={isCreatingExperiment}
          isUpdating={isUpdatingExperiment}
          onPrevious={handlePrevious}
          onContinue={() => {
            void handleContinue();
          }}
        />
      ),
    [
      currentStepKey,
      arePrototypeQuestionsReadyForFinalize,
      handleContinue,
      handlePrevious,
      isExperimentViewOnly,
      isCreatingExperiment,
      isLoading,
      isUpdatingExperiment,
      canOpenTagsFromPrototype,
      contextStepValidation.isValid,
      researchStepValidation.isValid,
    ],
  );

  const appShellHeaderContent = React.useMemo(
    () => ({
      centerContent: headerCenterContent,
      actions: null,
    }),
    [headerCenterContent],
  );

  useAppShellHeader(appShellHeaderContent);

  if (isLoading) {
    return (
      <Flex
        vertical
        align="center"
        justify="center"
        gap={16}
        style={{
          minHeight: "calc(100vh - 56px)",
          background: "#000000",
        }}
      >
        <Spin size="large" />
        <Typography.Text
          style={{
            color: "rgba(255, 255, 255, 0.6)",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Loading experiment...
        </Typography.Text>
      </Flex>
    );
  }

  const lockPageContentScroll =
    currentStepKey === "prototype" ||
    currentStepKey === "tags" ||
    currentStepKey === "finalize";

  return (
    <Flex
      vertical
      style={{
        background: "#000000",
        color: "#FFFFFF",
        height: "calc(100vh - 56px)",
        minHeight: "calc(100vh - 56px)",
        overflow: "hidden",
      }}
    >
      {submissionError || prototypeContinueError ? (
        <Flex
          style={{
            marginInline: "auto",
            marginTop: 16,
            width: "100%",
            maxWidth: 1280,
            paddingInline: 24,
          }}
        >
          <Alert
            showIcon
            type="error"
            message={prototypeContinueError ?? submissionError}
            style={{
              width: "100%",
              background: "rgba(245, 63, 63, 0.08)",
              border: "1px solid rgba(245, 63, 63, 0.3)",
              color: "#FFCCC7",
            }}
          />
        </Flex>
      ) : null}
      <Flex
        vertical
        style={{
          flex: 1,
          minHeight: 0,
          overflow: lockPageContentScroll ? "hidden" : "auto",
        }}
      >
        <Outlet />
      </Flex>
      {flowActions ? (
        <Flex
          align="center"
          style={{
            position: "sticky",
            bottom: 0,
            zIndex: 30,
            flexShrink: 0,
            width: "100%",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            background:
              "linear-gradient(180deg, rgba(8, 8, 8, 0.94), rgba(0, 0, 0, 0.98))",
            boxShadow: "0 -18px 40px rgba(0, 0, 0, 0.45)",
            padding: "14px 24px calc(14px + env(safe-area-inset-bottom))",
          }}
        >
          {flowActions}
        </Flex>
      ) : null}
    </Flex>
  );
};

export const ExperimentEditorLayout: React.FC = () => {
  const { experimentId } = useParams();

  return (
    <ExperimentEditorProvider experimentId={experimentId}>
      <ExperimentEditorShell />
    </ExperimentEditorProvider>
  );
};
